import { IQuote } from "../../shared/types";
import { isPricingStale } from "../../shared/pricingMonth";

export const quoteListErrorMessage = (error: unknown, apiBase = "") => {
  if (typeof error === "object" && error !== null && "isAxiosError" in error) {
    const axiosError = error as { message?: string; code?: string; response?: { status?: number; data?: any } };
    const responseData = axiosError.response?.data;
    const detail = typeof responseData === "string"
      ? responseData
      : responseData?.message || responseData?.error || axiosError.message || "Unknown error";
    if (!axiosError.response && axiosError.code === "ERR_NETWORK") {
      return `Could not connect to the CSP Admin API${apiBase ? ` at ${apiBase}` : ""}. Confirm the local API is running, then try again. (${detail})`;
    }
    return `CSP Admin API request failed${axiosError.response?.status ? ` (${axiosError.response.status})` : ""}: ${detail}`;
  }
  return error instanceof Error ? error.message : "Unknown error";
};

export type QuoteSortKey =
  | "quote_id"
  | "name"
  | "display_name"
  | "status"
  | "created_by_display_name"
  | "created_at"
  | "modified_by_display_name"
  | "modified_at"
  | "recent_activity";

export const recentActivity = (quote: IQuote) =>
  quote.modified_at || quote.created_at;

export const quoteDisplayStatus = (quote: IQuote, now = new Date()) => {
  const status = quote.status.toLowerCase();
  if (status === "void") return "VOID";
  if (status === "archived") return "ARCHIVED";
  if (quote.finalized_at) return "FINALIZED";
  if (isPricingStale(quote.last_priced_at, now)) return "EXPIRED";
  return "ACTIVE";
};

// Issue 46: a quote can be deleted only while it is active and has never been finalized - the
// same guard the API's usp_delete_quote enforces. A finalized, archived, or voided quote must be
// voided instead (or is already locked / gone).
export const quoteIsDeletable = (quote: IQuote) =>
  quote.status.toLowerCase() === "active" && !quote.finalized_at;

const csvValue = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const quoteListCsv = (quotes: IQuote[]) => {
  const rows = quotes.map(quote => [
    `CSP-CP-${quote.quote_id}`,
    quote.name || "Untitled quote",
    quote.display_name,
    // Issue 31: the affiliate name, so an export filtered by affiliate reads as a per-affiliate list.
    quote.affiliate_name || "",
    quoteDisplayStatus(quote),
    quote.modified_by_display_name || quote.created_by_display_name,
    quote.modified_at || quote.created_at,
  ]);
  return [["Reference #", "Quote", "Customer", "Affiliate", "Status", "Changed By", "Changed"], ...rows]
    .map(row => row.map(csvValue).join(","))
    .join("\r\n");
};

export const filterQuotes = (
  quotes: IQuote[],
  searchText: string,
  myQuotes: boolean,
  quoteType: "all" | "customer" | "prospect" = "all"
) => {
  const search = searchText.trim().toLocaleLowerCase();
  return quotes.filter((quote) => {
    if (myQuotes && !quote.is_created_by_current_user) return false;
    if (quoteType !== "all" && quote.quote_type !== quoteType) return false;
    if (!search) return true;
    return [
      `CSP-CP-${quote.quote_id}`,
      quote.name,
      quote.display_name,
      quote.prospect_email,
      // Issue 31: typing an affiliate name in the search box filters the list to that affiliate.
      quote.affiliate_name,
      quoteDisplayStatus(quote),
      quote.created_by_display_name,
      quote.modified_by_display_name,
    ].some((value) => value?.toLocaleLowerCase().includes(search));
  });
};
